// Per-day UNDO stack for persisted doctor moves/swaps/transfers. Each entry captures the prior doctor ids of the
// schedules a move changed, so the Undo button can restore them (and re-persist). Stored per day so it survives a
// refresh, like the room-transfer overlay it replaces. Cleared when a new simulation replaces the day.
import type { DoctorUndoChange, NurseUndoChange } from './doctorMoves.ts';
import type { GuardableAllocation } from './allocationWriteGuard.ts';

// A persisted move is either a DOCTOR reassignment or a NURSE (CRNA) one; `kind` tells the Undo handler which
// builder to replay it through. Older entries with no `kind` are treated as 'doctor' (the only kind that existed).
export interface MoveUndoEntry {
  label: string;
  kind?: 'doctor' | 'nurse';
  changes: (DoctorUndoChange | NurseUndoChange)[];
  /**
   * What the move LEFT the touched schedules looking like. `changes` stores absolute PRIOR ids, so replaying it
   * blindly force-writes yesterday's assignment onto whatever the day currently holds — after a wipe, a
   * re-allocate or another runner's edit that is a silent overwrite, not an undo. Recording the post-move state
   * lets the Undo handler verify the move is still the thing being undone and refuse when it isn't.
   * Optional: entries written before this field existed simply skip the check.
   */
  expected?: GuardableAllocation[];
}

export const moveUndoKey = (date: string) => `oc-move-undo-${date}`;

export function loadUndoStack(date: string): MoveUndoEntry[] {
  try {
    const raw = localStorage.getItem(moveUndoKey(date));
    if (raw) { const v = JSON.parse(raw); if (Array.isArray(v)) return v as MoveUndoEntry[]; }
  } catch { /* ignore */ }
  return [];
}

function save(date: string, stack: MoveUndoEntry[]): MoveUndoEntry[] {
  try { localStorage.setItem(moveUndoKey(date), JSON.stringify(stack)); } catch { /* ignore */ }
  return stack;
}

// Push a new undo entry (most recent last). Returns the updated stack.
export function pushUndo(date: string, entry: MoveUndoEntry): MoveUndoEntry[] {
  return save(date, [...loadUndoStack(date), entry]);
}

// Pop the most recent entry. Returns { entry, stack } — entry is null when the stack is empty.
export function popUndo(date: string): { entry: MoveUndoEntry | null; stack: MoveUndoEntry[] } {
  const stack = loadUndoStack(date);
  if (!stack.length) return { entry: null, stack };
  const entry = stack[stack.length - 1];
  return { entry, stack: save(date, stack.slice(0, -1)) };
}

// Clear the whole undo history for a date (used when a new simulation replaces the day).
export function clearUndo(date: string): void {
  try { localStorage.removeItem(moveUndoKey(date)); } catch { /* ignore */ }
}
